import { useCallback, useState } from 'react';
import { ProcessedQuote } from '@/types';

interface UseShareResult {
  share: (quote: ProcessedQuote) => Promise<void>;
  isSharing: boolean;
  shareMessage: string | null;
}

export const useShare = (): UseShareResult => {
  const [isSharing, setIsSharing] = useState(false);
  const [shareMessage, setShareMessage] = useState<string | null>(null);

  const share = useCallback(async (quote: ProcessedQuote) => {
    setIsSharing(true);
    
    try {
      const shareText = `${quote.text}\n\n—— ${quote.source}\n\n来自：每日一签`;
      
      // 尝试使用原生分享 API
      if (navigator.share) {
        await navigator.share({
          title: '每日一签',
          text: shareText,
          url: window.location.href
        });
        setShareMessage('分享成功！');
      } else {
        // 降级到剪贴板 API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(shareText);
          setShareMessage('已复制到剪贴板！');
        } else {
          // 最终降级：使用传统方法
          const textArea = document.createElement('textarea');
          textArea.value = shareText;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          try {
            document.execCommand('copy');
            setShareMessage('已复制到剪贴板！');
          } catch (err) {
            console.error('复制失败:', err);
            setShareMessage('分享失败，请手动复制文字');
          } finally {
            document.body.removeChild(textArea);
          }
        }
      }
    } catch (error) {
      console.error('分享失败:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        setShareMessage(null); // 用户取消了分享
      } else {
        setShareMessage('分享失败，请重试');
      }
    } finally {
      setIsSharing(false);
      
      // 3秒后清除消息
      if (shareMessage) {
        setTimeout(() => setShareMessage(null), 3000);
      }
    }
  }, [shareMessage]);

  return {
    share,
    isSharing,
    shareMessage
  };
};